import * as turf from "@turf/turf"
import { coordEach, type Units } from "@turf/turf"
import type {
	Feature,
	GeoJSON,
	GeoJsonProperties,
	Geometry,
	GeometryCollection,
	LineString,
	MultiLineString,
	MultiPoint,
	MultiPolygon,
	Point,
	Polygon,
	Position,
} from "geojson"
import { LngLat, LngLatBounds, Map } from "mapbox-gl"

export const coordinatesToLiteral = (coordinates: Position): LngLat => {
	return new LngLat(coordinates[0], coordinates[1])
}

export const coordinatesToText = (coordinates: Position | null | undefined, decimalPlaces?: number) => {
	if (!coordinates) return "(No Location)"
	const { lat, lng } = coordinatesToLiteral(coordinates)
	if (decimalPlaces) return `${lat.toFixed(decimalPlaces)}, ${lng.toFixed(decimalPlaces)}`
	return `${lat}, ${lng}`
}

export const coordinatesToUrlText = (coordinates: Position) => {
	const { lat, lng } = coordinatesToLiteral(coordinates)
	return `${lat}%2C${lng}`
}

export const openCoordsInGoogleMaps = (coordinates: Position) => {
	const url = `https://www.google.com/maps/search/?api=1&query=${coordinatesToUrlText(coordinates)}`
	window.open(url)
}

export const openDirectionsInGoogleMaps = (startingPoint: Position, destination: Position) => {
	const startingPointUrl = coordinatesToUrlText(startingPoint)
	const destinationUrl = coordinatesToUrlText(destination)
	const url = `https://www.google.com/maps/dir/?api=1&origin=${startingPointUrl}&destination=${destinationUrl}`
	window.open(url)
}

export const transformStretch = <T extends GeoJSON | GeometryCollection>(
	geojson: T,
	// TODO: could add more origin options what what the shape is being scaled against, ex. point instead of line
	line: LineString | Feature<LineString>,
	distance: number,
	bearing: number,
): T => {
	const clone = turf.clone(geojson)

	let maxDistance: number

	coordEach(clone, (coords) => {
		const distance = turf.pointToLineDistance(coords, line)

		if (!maxDistance || distance > maxDistance) {
			maxDistance = distance
		}
	})

	coordEach(clone, (coords) => {
		const distanceToLine = turf.pointToLineDistance(coords, line)
		const newCoords = turf.getCoord(turf.destination(coords, (distanceToLine / maxDistance) * distance, bearing))
		coords[0] = newCoords[0]
		coords[1] = newCoords[1]
	})

	return clone
}

export const wrapGeometryInFeature = <TGeometry extends Geometry, TProperties extends GeoJsonProperties>(
	geometry: TGeometry,
	properties: TProperties,
): GeoJSON.Feature<TGeometry, TProperties> => {
	return {
		type: "Feature",
		geometry: geometry,
		properties: properties,
	}
}

export const unwrapGeometryFromFeature = <TGeometry extends Geometry, TProperties extends GeoJsonProperties>(
	feature: GeoJSON.Feature<TGeometry, TProperties>,
): TGeometry => {
	return feature.geometry
}

export function computePointForGeometry(geometry: Polygon | Point | LineString): Point {
	switch (geometry.type) {
		case "Point":
			return geometry
		case "LineString": {
			const lineLength = turf.length(turf.lineString(geometry.coordinates))
			return turf.along(geometry, lineLength / 2).geometry
		}
		case "Polygon":
			return turf.center(geometry).geometry
	}
}

export function isGroupGeoemtry(
	geometry: Geometry,
): geometry is MultiPoint | MultiLineString | MultiPolygon | GeometryCollection {
	switch (geometry.type) {
		case "Point":
		case "LineString":
		case "Polygon":
			return false
		case "MultiPoint":
		case "MultiLineString":
		case "MultiPolygon":
		case "GeometryCollection":
			return true
	}
}

export function isPoints(geometries: Geometry[]): geometries is Point[] {
	return geometries.every((geometry) => geometry.type === "Point")
}

export function isLineStrings(geometries: Geometry[]): geometries is LineString[] {
	return geometries.every((geometry) => geometry.type === "LineString")
}

export function isPolygons(geometries: Geometry[]): geometries is Polygon[] {
	return geometries.every((geometry) => geometry.type === "Polygon")
}

function convertPositionTo2D(position: Position): Position {
	return [position[0], position[1]]
}

export function convertGeometryTo2D(geometry: Geometry) {
	switch (geometry.type) {
		case "Point":
			geometry.coordinates = convertPositionTo2D(geometry.coordinates)
			break
		case "MultiPoint":
			geometry.coordinates = geometry.coordinates.map(convertPositionTo2D)
			break
		case "LineString":
			geometry.coordinates = geometry.coordinates.map(convertPositionTo2D)
			break
		case "MultiLineString":
			geometry.coordinates = geometry.coordinates.map((coordinates) => coordinates.map(convertPositionTo2D))
			break
		case "Polygon":
			geometry.coordinates = geometry.coordinates.map((coordinates) => coordinates.map(convertPositionTo2D))
			break
		case "MultiPolygon":
			geometry.coordinates = geometry.coordinates.map((coordinates) =>
				coordinates.map((coordinates) => coordinates.map(convertPositionTo2D)),
			)
			break
		case "GeometryCollection":
			geometry.geometries.map((geometry) => convertGeometryTo2D(geometry))
			break
	}
}

export function unwrapGeometryCollections(geometry: Geometry): Exclude<Geometry, GeometryCollection>[] {
	switch (geometry.type) {
		case "Point":
		case "LineString":
		case "Polygon":
		case "MultiPoint":
		case "MultiLineString":
		case "MultiPolygon":
			return [geometry]
		case "GeometryCollection":
			return geometry.geometries.flatMap((geometry) => unwrapGeometryCollections(geometry))
	}
}

export function flattenGeometry(geometry: Geometry): Extract<Geometry, Point | LineString | Polygon>[] {
	return unwrapGeometryCollections(geometry)
		.flatMap((geometry) => turf.flatten(geometry))
		.flatMap((fc) => fc.features.map(({ geometry }) => geometry))
}

export function square(
	center: number[] | Point | Feature<Point, GeoJsonProperties>,
	radius: number,
	options: { units?: Units; properties?: GeoJsonProperties } = {},
) {
	return turf.bboxPolygon(turf.bbox(turf.circle(center, radius, { steps: 4, ...options })))
}

export function pixelDistanceToDistance(map: Map, pixelDistance: number, options: { units?: Units } = {}) {
	const center = map.getCenter()

	const centerPixel = map.project(center)

	const offsetLngLat = map.unproject([centerPixel.x + pixelDistance, centerPixel.y])

	return turf.distance([center.lng, center.lat], [offsetLngLat.lng, offsetLngLat.lat], options)
}

export function polygonToLngLatBounds(polygon: Polygon) {
	const [lngSw, latSw, lngNe, latNe] = turf.bbox(polygon)
	return new LngLatBounds([lngSw, latSw, lngNe, latNe])
}
